import { APIEndpoints } from "@/data/constants/endpoints";
import { Request } from "@/data/api/utils/network";
import { RequestParams } from "@/data/types";
import {
  TNaerasAdjustPayload,
  TNaerasBidSearchParams,
  TNaerasCalculatePayload,
  TNaerasFinalizePayload,
  TNaerasJob,
  TNaerasPeziWaterParams,
  TNaerasPoolUpdatePayload,
} from "@/data/types";

/**
 * Get the date picker window: today (Greek calendar), the selectable range,
 * the suggested pre-selection and the Greek public holidays.
 * @param params Request parameters, plus optional `from` / `to` holiday range
 */
export const getNaerasCalendar = (params: RequestParams & { from?: string; to?: string }) => {
  return Request({
    path: APIEndpoints.NAERAS.CALENDAR,
    type: "GET",
    params,
  });
};

/**
 * List bids for the history table
 * @param params Search parameters. `target_date` is a comma-separated `from,to` range.
 */
export const getNaerasBids = (params: TNaerasBidSearchParams) => {
  return Request({
    path: APIEndpoints.NAERAS.BIDS,
    type: "GET",
    params,
  });
};

/**
 * Get the bid of a single day, with its adjustment limits
 * @param targetDate The day the bid is for, YYYY-MM-DD
 * @param params Request parameters
 */
export const getNaerasBid = (targetDate: string, params: RequestParams) => {
  return Request({
    path: APIEndpoints.NAERAS.BID_BY_DATE(targetDate),
    type: "GET",
    params,
  });
};

/**
 * Calculate the bid for the selected days. One NAERAS call covers all of them,
 * so this is slow — never fire it more than once at a time.
 * @param params Request parameters
 * @param payload The selected dates, and whether to discard existing rows
 */
export const calculateNaerasBids = (
  params: RequestParams,
  payload: TNaerasCalculatePayload
) => {
  return Request({
    path: APIEndpoints.NAERAS.BIDS,
    type: "POST",
    params,
    body: payload,
  });
};

/**
 * Save the user's adjustments. Parts left out keep their current value, and
 * each array sent is taken whole (all 24 values).
 * @param targetDate The day being adjusted, YYYY-MM-DD
 * @param params Request parameters
 * @param payload The changed parts of the program
 */
export const adjustNaerasBid = (
  targetDate: string,
  params: RequestParams,
  payload: TNaerasAdjustPayload
) => {
  return Request({
    path: APIEndpoints.NAERAS.BID_BY_DATE(targetDate),
    type: "PATCH",
    params,
    body: payload,
  });
};

/**
 * Download the .xlsx workbook that gets sent to ΔΕΔΔΗΕ, one sheet per date.
 * Auth headers are required, so this has to be fetched as a blob.
 * @param dates 1-7 dates, YYYY-MM-DD
 * @param params Request parameters
 */
export const exportNaerasBids = (dates: string[], params: RequestParams) => {
  return Request({
    path: APIEndpoints.NAERAS.BIDS_EXPORT,
    type: "GET",
    params: { ...params, dates: dates.join(",") },
    responseType: "blob",
  });
};

/**
 * Upload the Excel file that was actually sent to ΔΕΔΔΗΕ. This only records the
 * values as a draft — it never locks a day. A day that is not final yet becomes
 * `adjusted`; one that already is keeps its `final` values and gains a pending
 * revision. Either way, `finalizeNaerasBids` is what applies it.
 * @param file The .xlsx file
 * @param params Request parameters
 */
export const importNaerasBids = (file: File, params: RequestParams) => {
  const form = new FormData();
  form.append("file", file);

  return Request({
    path: APIEndpoints.NAERAS.BIDS_IMPORT,
    type: "POST",
    params,
    body: form,
    contentType: "multipart/form-data",
  });
};

/**
 * Lock the given days as `final`, using each one's current draft — the uploaded
 * or adjusted values, otherwise the calculated ones. The same call revises a
 * day that is already final when a newer Excel has been uploaded for it, and
 * moves the resulting hybrid error onto the latest final bid. All-or-nothing:
 * if any day is rejected, nothing changes.
 * @param params Request parameters
 * @param payload The days to lock, 1-31, in any order and possibly in the past
 */
export const finalizeNaerasBids = (
  params: RequestParams,
  payload: TNaerasFinalizePayload
) => {
  return Request({
    path: APIEndpoints.NAERAS.BIDS_FINALIZE,
    type: "POST",
    params,
    body: payload,
  });
};

/**
 * Get the job-run history. Paginated by cursor, not by page number.
 * @param params Search parameters, including `cursor` from a previous page
 */
export const getNaerasJobRuns = (
  params: RequestParams & { job?: TNaerasJob; status?: string; cursor?: string }
) => {
  return Request({
    path: APIEndpoints.NAERAS.JOB_RUNS,
    type: "GET",
    params,
  });
};

/**
 * Get the hybrid pool in effect at 00:00 of a date, and how stale it is
 * @param params Request parameters, plus the optional `date` (defaults to today)
 */
export const getNaerasPool = (params: RequestParams & { date?: string }) => {
  return Request({
    path: APIEndpoints.NAERAS.JOB_RUNS_POOL,
    type: "GET",
    params,
  });
};

/**
 * Set the hybrid pool at 00:00 of a day by hand. Answers with the same body as
 * the GET for that day.
 * @param params Request parameters
 * @param payload The day (today or earlier) and the value in MWh
 */
export const updateNaerasPool = (params: RequestParams, payload: TNaerasPoolUpdatePayload) => {
  return Request({
    path: APIEndpoints.NAERAS.JOB_RUNS_POOL,
    type: "PATCH",
    params,
    body: payload,
  });
};

/**
 * Remove a day's manual pool value, returning the day to what Job A computed.
 * Not an error on a day without one — it just answers with the current pool.
 * @param params Request parameters, plus the `date` to revert
 */
export const revertNaerasPool = (params: RequestParams & { date: string }) => {
  return Request({
    path: APIEndpoints.NAERAS.JOB_RUNS_POOL,
    type: "DELETE",
    params,
  });
};

/**
 * Get a single job run. Resolves to `null` when the job hasn't run that day.
 * @param job Which job, A / B / C
 * @param runDate The day the job ran, YYYY-MM-DD
 * @param params Request parameters
 */
export const getNaerasJobRun = (job: TNaerasJob, runDate: string, params: RequestParams) => {
  return Request({
    path: APIEndpoints.NAERAS.JOB_RUN(job, runDate),
    type: "GET",
    params,
  });
};

/**
 * Get today's dashboard: six panels, each available or not on its own.
 * Read-only, no parameters — "today" is the Greek calendar day.
 * @param params Request parameters
 */
export const getNaerasDashboard = (params: RequestParams) => {
  return Request({
    path: APIEndpoints.NAERAS.DASHBOARD,
    type: "GET",
    params,
  });
};

/**
 * Measured water taken from Πέζι per day, gaps included — for the CSV export
 * and for more history than the dashboard carries. At most 366 days.
 * @param params Request parameters, plus the optional `from` / `to` range
 */
export const getNaerasPeziWater = (params: RequestParams & TNaerasPeziWaterParams) => {
  return Request({
    path: APIEndpoints.NAERAS.DASHBOARD_PEZI_WATER,
    type: "GET",
    params,
  });
};

/**
 * Get the wet-season switch, and whether the next bid can still be changed by it
 * @param params Request parameters
 */
export const getNaerasSettings = (params: RequestParams) => {
  return Request({
    path: APIEndpoints.NAERAS.SETTINGS,
    type: "GET",
    params,
  });
};

/**
 * Turn the wet-season switch on or off. Answers with the same body as the GET;
 * outside 1/10–15/4 it is refused with NAERAS_WET_SEASON_LOCKED.
 * @param params Request parameters
 * @param payload `wet_season` as a JSON boolean — the string "false" is a 400
 */
export const updateNaerasSettings = (params: RequestParams, payload: { wet_season: boolean }) => {
  return Request({
    path: APIEndpoints.NAERAS.SETTINGS,
    type: "PATCH",
    params,
    body: payload,
  });
};
